import type { ColorLockedLegendItem } from "store/features/app/appSlice";
import { removeCount } from "utils/removeCountFromTitle";

import type { MapLegendItem } from "api/map";

import type { IColorPalette } from "models/colorPalette";

import {
  NA_MATCH_KEY,
  buildLockedColorKey,
  isExcludedBinLabel,
  isOthersBinLabel,
  normalizeColorKey,
  normalizeMatchKey
} from "./operatorKeys";
import { RigFracFocusAttribute, getFocusSyncTrigger } from "./triggers";

export type FocusSyncState = "inactive" | "pending" | "active";

/**
 * Where the operator-variant translation stands. `failed` is kept distinct
 * from `loading` on purpose: a request that will never succeed must not look
 * the same as one that has not answered yet.
 */
export type FocusTranslationStatus = "not-needed" | "loading" | "resolved" | "failed";

export interface FocusColorResolution {
  state: FocusSyncState;
  /** Which rig/frac attribute this focus matches. Undefined only when inactive. */
  attribute?: RigFracFocusAttribute;
  /** normalized match key -> hex, from the focus bins. Empty unless active. */
  colors: ReadonlyMap<string, string>;
  /** normalized hexes the focus bins consume, matched or not. Empty unless active. */
  reservedColors: ReadonlySet<string>;
  /** The user's active well palette, to spread over unmatched values. Only set while active. */
  basePalette?: IColorPalette;
  /** normalized match key -> focus bin label, for every named bin. Empty unless active. */
  binOfKey: ReadonlyMap<string, string>;
  /**
   * normalized focus bin label -> hex. A chart bar mapped onto a bin is labelled
   * with the bin, a spelling `colors` deliberately never carries. Empty unless active.
   */
  binColors: ReadonlyMap<string, string>;
  /** Whether the focus legend has an Others row, which is what folds chart bins into Others. */
  hasOthers?: boolean;
  /** The Others colour to paint: a locked one, or the legend's when Others is in filter. */
  othersColor?: string;
}

// The backend returns a count-0, not-in-filter row per excluded value so the
// legend can show a red X; it is not a real bin and must not be coloured.
const isExcludedPlaceholder = (item: MapLegendItem) =>
  (item.count ?? 0) === 0 && !item.inFilter;

const EMPTY_COLORS: ReadonlyMap<string, string> = new Map();
const EMPTY_RESERVED_COLORS: ReadonlySet<string> = new Set();
const EMPTY_BIN_MAP: ReadonlyMap<string, string> = new Map();

// Frozen module-level singleton returned by identity for the genuinely
// inactive case (no trigger in effect), so consumer memos and effect
// dependency arrays never see this as "changed". A pending result always
// carries a real `attribute`, so it can never reuse this singleton — only
// `inactive` ever does.
export const INACTIVE_FOCUS_COLOR_RESOLUTION: FocusColorResolution = Object.freeze({
  state: "inactive",
  attribute: undefined,
  colors: EMPTY_COLORS,
  reservedColors: EMPTY_RESERVED_COLORS,
  basePalette: undefined,
  binOfKey: EMPTY_BIN_MAP,
  binColors: EMPTY_BIN_MAP
});

export interface BuildFocusColorResolutionParams {
  /** The well focus (group-by) property currently in effect. */
  property: Maybe<string>;
  /** The well legend's current bins, in legend order. */
  legendItems: Maybe<MapLegendItem[]>;
  lockedColors: Record<string, ColorLockedLegendItem>;
  /**
   * normalized variant value -> public-record operator name, from the
   * two-column translation query (see useFocusTranslation). Required only
   * when the active trigger's needsTranslation is true; ignored otherwise.
   */
  translations?: Maybe<ReadonlyMap<string, string>>;
  /** Every public-record name per normalized variant value, with its well count. Bins only. */
  allTranslations?: Maybe<ReadonlyMap<string, ReadonlyMap<string, number>>>;
  /** Whether that translation is still coming, already here, or failed outright. */
  translationStatus?: FocusTranslationStatus;
  /** True while a wells map request is outstanding, so the bins may be replaced. */
  isWellsRequestInFlight?: boolean;
  /**
   * True when the legend on hand was built for a different focus property than
   * the one now in effect. This covers the window a request flag cannot: the
   * focus changes, the refetch is debounced, and until it lands the bins belong
   * to the previous field while carrying this field's name.
   */
  isLegendStale?: boolean;
  /** The user's active well palette. Carried onto the result only while active. */
  basePalette?: Maybe<IColorPalette>;
}

/**
 * Turns the well legend into a rig/frac colour resolution, only when the
 * active well focus is one of the focus sync triggers. Per legend item, in
 * legend order: derive the value the same way LegendItemModel does (never
 * the raw title, which carries a leading count), skip rollup/placeholder
 * entries, apply any colour lock/preferred colour on top of the bin colour
 * through the same `${property}:${value}` keyed lookup the legend panel
 * already uses, reserve that colour unconditionally (so unmatched values
 * keep a stable colour as a bbox-scoped chart pans), translate to the
 * public-record operator name when the trigger requires it, and keep the
 * first entry for a given key when two bins collapse under trim + case
 * folding.
 *
 * `pending` means one thing only: a request is outstanding that will change
 * this answer, so surfaces should keep showing what they already show rather
 * than paint something about to be replaced. It is deliberately never used
 * for a state that has nowhere to go — a translation that failed, or an empty
 * legend with nothing on the way, both resolve to `inactive` and let the
 * default palette show. Holding indefinitely would leave rig/frac colours
 * frozen with no way back.
 *
 * `inactive` is also what a focus moving off these fields returns, via the
 * frozen singleton — the case callers revert on immediately.
 */
export function buildFocusColorResolution(
  params: BuildFocusColorResolutionParams
): FocusColorResolution {
  const {
    property,
    legendItems,
    lockedColors,
    translations,
    allTranslations,
    translationStatus,
    isWellsRequestInFlight,
    isLegendStale,
    basePalette
  } = params;

  const trigger = getFocusSyncTrigger(property);
  if (!trigger) {
    return INACTIVE_FOCUS_COLOR_RESOLUTION;
  }

  const pending: FocusColorResolution = {
    state: "pending",
    attribute: trigger.attribute,
    colors: EMPTY_COLORS,
    reservedColors: EMPTY_RESERVED_COLORS,
    basePalette: undefined,
    binOfKey: EMPTY_BIN_MAP,
    binColors: EMPTY_BIN_MAP
  };

  if (trigger.needsTranslation) {
    if (translationStatus === "loading") {
      return pending;
    }
    // Failed, or resolved to nothing usable. Either way nothing is coming, so
    // fall back to the default palette instead of holding forever.
    if (translations == null) {
      return INACTIVE_FOCUS_COLOR_RESOLUTION;
    }
  }

  // Hold while the bins on hand cannot be trusted to describe this focus:
  // either a wells request is out and will replace them, or they are known to
  // have been built for a different property and the refetch has not landed
  // yet. Colouring from the wrong field's bins is worse than briefly holding,
  // because nothing matches and every value moves to a base-palette colour it
  // is about to lose again.
  if (isWellsRequestInFlight || isLegendStale) {
    return pending;
  }

  // No bins and nothing on the way — there is nothing to match against, and
  // an empty legend is a resting state (hiding the wells layer produces one),
  // so this must not hold.
  const isLegendEmpty = !legendItems || legendItems.length === 0;
  if (isLegendEmpty) {
    return INACTIVE_FOCUS_COLOR_RESOLUTION;
  }

  const colors = new Map<string, string>();
  const reservedColors = new Set<string>();
  // count of the bin currently winning each key, so a collision (e.g. "APACHE"
  // and "Apache" both normalizing to "apache") is resolved by which bin the
  // legend actually represents more wells, not by legend ordering.
  const winningCounts = new Map<string, number>();
  const binOfKey = new Map<string, string>();
  const binKeyCounts = new Map<string, number>();
  const binColors = new Map<string, string>();
  let othersColor: Maybe<string>;
  let hasOthers = false;

  // When two bins are spelled alike (APACHE, Apache), the one with more wells
  // (the first on a tie) owns that spelling's colour and company names, so they
  // agree with the label binOfKey picks whatever the legend order.
  const winningBinOfKey = new Map<string, { label: string; count: number }>();
  for (const item of legendItems) {
    if (isExcludedPlaceholder(item)) continue;
    const rawValue = removeCount(item.groupTitle);
    if (isExcludedBinLabel(rawValue)) continue;
    const key = normalizeMatchKey(rawValue);
    const count = item.count ?? 0;
    const winner = winningBinOfKey.get(key);
    if (!winner || count > winner.count)
      winningBinOfKey.set(key, { label: rawValue, count });
  }

  const addBinKey = (key: string, label: string, count: number) => {
    const winningCount = binKeyCounts.get(key);
    if (winningCount === undefined || count > winningCount) {
      binOfKey.set(key, label);
      binKeyCounts.set(key, count);
    }
  };

  for (const item of legendItems) {
    if (isExcludedPlaceholder(item)) continue;
    const rawValue = removeCount(item.groupTitle);
    const count = item.count ?? 0;
    if (isOthersBinLabel(rawValue)) {
      hasOthers = true;
      // map-svc only styles in-filter rows, so a not-in-filter Others carries
      // the StyleSet fallback colour, not a real one; the chart keeps its own.
      othersColor =
        lockedColors[buildLockedColorKey(trigger.property, rawValue)]?.color ??
        (item.inFilter ? item.color : undefined);
      continue;
    }
    if (normalizeMatchKey(rawValue) === NA_MATCH_KEY) {
      // The focus shows n/a as a bin, so the chart's own n/a bar must not fold.
      binOfKey.set(NA_MATCH_KEY, NA_MATCH_KEY);
      continue;
    }
    if (isExcludedBinLabel(rawValue)) {
      continue;
    }

    const lockKey = buildLockedColorKey(trigger.property, rawValue);
    // map-svc only styles in-filter rows; a not-in-filter row carries the
    // StyleSet fallback colour, so only a lock gives it a real one.
    const color =
      lockedColors[lockKey]?.color ?? (item.inFilter ? item.color : undefined);

    // Every eligible bin reserves its colour, whether or not it ends up
    // translated/matched — the reserved set exists to keep unmatched values
    // stable, not to describe what matched.
    if (color) reservedColors.add(normalizeColorKey(color));

    const rawKey = normalizeMatchKey(rawValue);
    const ownsSpelling = winningBinOfKey.get(rawKey)?.label === rawValue;
    if (ownsSpelling && color) binColors.set(rawKey, color);
    // Ranked by the bin total, so a bin's own spelling beats another bin's company name.
    addBinKey(rawKey, rawValue, count);
    if (trigger.needsTranslation && ownsSpelling) {
      // Ranked by the company's own wells under this value, not the bin total,
      // so a company shared by two values goes where the focus list puts most of it.
      // Summed per match key first, so "Acme" and "ACME" count as one company.
      const companyCounts = new Map<string, number>();
      for (const [name, pairCount] of allTranslations?.get(rawKey) ?? []) {
        const key = normalizeMatchKey(name);
        companyCounts.set(key, (companyCounts.get(key) ?? 0) + pairCount);
      }
      companyCounts.forEach((pairCount, key) => addBinKey(key, rawValue, pairCount));
    }

    const matchValue = trigger.needsTranslation ? translations?.get(rawKey) : rawValue;
    if (matchValue == null) {
      // No public-record translation for this bin — the same "no match"
      // outcome as a name that simply doesn't overlap with any rig/frac value.
      continue;
    }

    for (const value of trigger.toRigFracValues?.(matchValue) ?? [matchValue]) {
      const key = normalizeMatchKey(value);
      const winningCount = winningCounts.get(key);
      if (color && (winningCount === undefined || count > winningCount)) {
        colors.set(key, color);
        winningCounts.set(key, count);
      }
      if (!trigger.needsTranslation) {
        addBinKey(key, rawValue, count);
      }
    }
  }

  return {
    state: "active",
    attribute: trigger.attribute,
    colors,
    reservedColors,
    basePalette: basePalette ?? undefined,
    // With no company names, only short names would be sent, no rig/frac label
    // would match, and the chart would fold into one Others bar.
    binOfKey:
      trigger.needsTranslation && !allTranslations?.size ? EMPTY_BIN_MAP : binOfKey,
    binColors,
    hasOthers,
    othersColor
  };
}
